import React, { useState, useEffect } from "react";
import ProjectCard from "@/components/ProjectCard";
import BottomNavigation from "@/components/BottomNavigation";
import { Project } from "@/types";
import { useNavigate } from "react-router-dom";
import { projectsAPI } from "@/api/projects";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { ScreenHeader } from "@/components/redesign";
import { Loader2 } from "lucide-react";

// Helper function to map database project format to the Project type (reused from HomePage)
const mapDatabaseProjectToProjectType = async (dbProject: any): Promise<Project> => {
  // Get the detailed costs for the project
  let detailedCosts = null;
  try {
    detailedCosts = await projectsAPI.getProjectCosts(dbProject.id);
  } catch (error) {
    detailedCosts = null;
  }
  
  // Get upcoming check payments for the project
  let upcomingPayments = [];
  try {
    upcomingPayments = await projectsAPI.getProjectUpcomingCheckPayments(dbProject.id);
  } catch (error) {
    upcomingPayments = [];
  }

  // Prepare basic project data with try-catch for safety
  try {
    // Helper function to safely access nested properties
    const safeGet = (obj: any, path: string, defaultValue: any = 0) => {
      const parts = path.split('.');
      let current = obj;
      
      for (const part of parts) {
        if (current == null || typeof current !== 'object') {
          return defaultValue;
        }
        current = current[part];
      }
      
      return current ?? defaultValue;
    };
    
    const project: Project = {
      id: dbProject.id,
      name: dbProject.name,
      status: dbProject.status || "completed",
      progress: dbProject.progress || 100,
      costs: {
        try: detailedCosts ? safeGet(detailedCosts, 'total.TRY', dbProject.cost_try || 0) : (dbProject.cost_try || 0),
        usd: detailedCosts ? safeGet(detailedCosts, 'total.USD', dbProject.cost_usd || 0) : (dbProject.cost_usd || 0),
        eur: detailedCosts ? safeGet(detailedCosts, 'total.EUR', dbProject.cost_eur || 0) : (dbProject.cost_eur || 0),
      },
      details: {
        parcel: dbProject.parcel || "",
        lot: dbProject.lot || "",
        flats: dbProject.flats || 0,
        shops: dbProject.shops || 0,
        area: dbProject.area || 0,
        costPerSquareMeter: dbProject.cost_per_square_meter || 0,
      },
      upcomingPayments: upcomingPayments
    };

    // If we have detailed costs with the expected structure, use them
    if (detailedCosts && detailedCosts.official && detailedCosts.unofficial && detailedCosts.total) {
      project.detailedCosts = {
        official: {
          TRY: safeGet(detailedCosts, 'official.TRY', 0),
          USD: safeGet(detailedCosts, 'official.USD', 0),
          EUR: safeGet(detailedCosts, 'official.EUR', 0),
        },
        unofficial: {
          TRY: safeGet(detailedCosts, 'unofficial.TRY', 0),
          USD: safeGet(detailedCosts, 'unofficial.USD', 0),
          EUR: safeGet(detailedCosts, 'unofficial.EUR', 0),
        },
        total: {
          TRY: safeGet(detailedCosts, 'total.TRY', 0),
          USD: safeGet(detailedCosts, 'total.USD', 0),
          EUR: safeGet(detailedCosts, 'total.EUR', 0),
        },
      };
    }
    
    return project;
  } catch (error) {
    // Return a minimal valid project object on error
    return {
      id: dbProject.id,
      name: dbProject.name || "Error loading project",
      status: "completed",
      progress: 100,
      costs: { try: 0, usd: 0, eur: 0 },
      details: { parcel: "", lot: "", flats: 0, shops: 0, area: 0, costPerSquareMeter: 0 },
      upcomingPayments: []
    };
  }
};

const CompletedProjectsPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  const [completedProjects, setCompletedProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompletedProjects = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        setErrorMessage(null);
        
        // Fetch all projects
        try {
          const projectsData = await projectsAPI.getProjects();
          
          // Check if projectsData is an array before mapping
          if (Array.isArray(projectsData)) {
            // Filter for completed projects
            const completed = projectsData.filter(project => project.status === 'completed');
            
            const projectPromises = completed.map(mapDatabaseProjectToProjectType);
            const mappedProjects = await Promise.all(projectPromises);
            setCompletedProjects(mappedProjects);
          } else {
            setCompletedProjects([]);
          }
        } catch (projectsError) {
          toast({
            title: t('common.error'),
            description: t('errors.projects_loading_error') + ": " + 
              (projectsError instanceof Error ? projectsError.message : t('errors.unknown_error')),
            variant: "destructive",
          });
          setHasError(true);
          setErrorMessage((projectsError instanceof Error ? projectsError.message : t('errors.projects_loading_error')));
          setCompletedProjects([]);
        }
      } catch (error) {
        toast({
          title: t('common.error'),
          description: t('errors.data_load_error') + ": " + 
            (error instanceof Error ? error.message : t('errors.unknown_error')),
          variant: "destructive",
        });
        setHasError(true);
        setErrorMessage((error instanceof Error ? error.message : t('errors.data_load_error')));
      } finally {
        setIsLoading(false);
      }
    };

    fetchCompletedProjects();
    
    // Add listener for project data refresh events
    const handleProjectDataRefresh = () => {
      fetchCompletedProjects();
    };
    
    document.addEventListener('project-data-refresh', handleProjectDataRefresh);
    
    return () => {
      document.removeEventListener('project-data-refresh', handleProjectDataRefresh);
    };
  }, [toast, t]);

  return (
    <div className="page-container bg-gray-25">
      <ScreenHeader
        title={t('projects.completed_projects')}
        onBack={() => navigate('/')}
      />

      <main className="flex-1 overflow-y-auto px-4 py-4 pb-20">
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="h-7 w-7 animate-spin text-brand-600 mb-3" />
              <p className="text-sm text-gray-500">{t('projects.loading_projects')}</p>
            </div>
          ) : hasError ? (
            <div className="bg-white border border-error-600/20 rounded-2xl p-6 shadow-sm">
              <h3 className="text-base font-semibold text-error-600 mb-2">{t('errors.data_load_error')}</h3>
              <p className="text-sm text-gray-500 mb-2">
                {errorMessage || t('errors.connection_error')}
              </p>
              <p className="text-xs text-gray-400">
                {t('notifications.please_try_again')}
              </p>
            </div>
          ) : completedProjects.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
              <h3 className="font-bold text-lg text-gray-900 mb-2">{t('projects.no_completed_projects')}</h3>
              <p className="text-gray-500 text-sm">{t('projects.no_completed_projects_description')}</p>
            </div>
          ) : (
            completedProjects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))
          )}
        </div>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default CompletedProjectsPage; 